import {
  BookingApi,
  BookingBookingBillingRead,
  BookingHotelRoomBookingWrite,
  BookingMeetingRoomBookingWrite,
  BookingRemarkBookingWrite,
  BookingRemarkBookingWriteTypeEnum,
  BookingWrite,
  LocationRead,
  LocationReservationRead,
  ReservationApi,
  ReservationDayHallRead,
  ReservationDayRead,
  ReservationDayReservationWrite,
  ReservationRead,
  ReservationReadTypesEnum,
  ReservationRemarkReservationWrite,
  ReservationRemarkWriteTypeEnum,
  ReservationRoomRead,
  ReservationWrite,
  ReservationWriteSourceEnum,
} from '@/api';
import helpers from '@/services/helpers';
import { State } from '@/store/index';
import axios from 'axios';
import { add, eachDayOfInterval, format, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash-es';
import { Module } from 'vuex';

function getReservationApiBypass(): ReservationApi {
  return new ReservationApi(
    undefined,
    import.meta.env.VITE_PROXY_BYPASS_URL,
    axios,
  );
}

function getReservationApi(): ReservationApi {
  return new ReservationApi(undefined, import.meta.env.VITE_PROXY_URL, axios);
}

function getBookingApi(): BookingApi {
  return new BookingApi(undefined, import.meta.env.VITE_PROXY_URL, axios);
}

const getDefaultState = () => ({
  bookingToken: null as string | null,
  bookingId: null as null | number | undefined,
  transitions: [] as string[],
  reservation: {
    days: [],
    rooms: [],
    location: {
      name: '',
    },
    meeting: {
      name: '',
    },
  } as ReservationRead,
  config: {
    headers: {
      username: '' as string,
      key: '' as string,
    },
    withCredentials: false,
  } as Record<string, any>,
  reservationLoading: false as boolean,
  reservationSubmitting: false as boolean,
});

function parseBookingData(booking: BookingBookingBillingRead): ReservationRead {
  return parseReservationData({
    ...booking,
    street: booking.address,
    user: {
      email: booking.email,
    },
    contactpersonFirstname: booking.contactPersonFirstname,
    contactpersonLastname: booking.contactPersonLastname,
    phonenumber: booking.contactPersonPhonenumber,
    days: booking.meetingRooms?.map((day) => ({
      ...day,
      items: day.items?.map((item) => ({
        ...item,
        item: item.itemObject,
      })),
      halls: day.halls?.map((hall) => ({
        ...hall,
        emplacement: hall.emplacementObject,
      })),
      packageDeals: day.packageDeals?.map((deal) => ({
        ...deal,
        packageDeal: {
          ...deal.packageDealObject,
          items:
            deal.packageDealObject?.items?.map((item) => ({
              ...item,
              item: item.itemObject,
            })) ?? [],
          packageDeal: deal.packageDealObject,
        },
      })),
    })),
    meeting: booking.meetingTypeObject ?? booking.eventTypeRead,
    rooms: booking.hotelRooms,
    location: booking.locationObject,
  });
}

function parseReservationDay(
  day: BookingMeetingRoomBookingWrite,
): ReservationDayReservationWrite {
  return {
    ...day,
    halls: day.halls?.map((hall) => ({
      ...hall,
      emplacement: helpers.getIRI(hall.emplacement, '/api/emplacements/'),
      pax: Math.round(hall.pax ?? 1),
      hall: helpers.getIRI(hall.hall, '/api/halls/'),
    })),
    items: day.items?.map((item) => ({
      ...item,
      item: helpers.getIRI(item.item, '/api/items/') ?? null,
      amount: Math.round(item.amount ?? 1),
    })),
    packageDeals: day.packageDeals?.map((packageDeal) => ({
      packageDeal:
        helpers.getIRI(packageDeal.packageDeal, '/api/package_deals/') ?? null,
      amount: day.pax,
    })),
  };
}

function parseReservationDataBeforeRequest(
  data: Record<string, any>,
): ReservationWrite {
  data = JSON.parse(JSON.stringify(data));
  data.remarks = [];
  data.days = data.days?.map(parseReservationDay);
  if (data.rooms) {
    const breakfastSelectable =
      data.location.locationSettings.breakfastSelectable;
    const breakfastDefault =
      data.location.locationSettings.breakfastEnabledByDefault;
    data.rooms.forEach((room: Record<string, any>) => {
      room.breakfast = breakfastSelectable
        ? (room.breakfast ?? breakfastDefault)
        : breakfastDefault;
      room.amount = Math.round(room.amount);
      room.paxRoom = Math.round(room.paxRoom);
      room.roomType = room.locationRoomType;
      if (room.comments) {
        const remark: ReservationRemarkReservationWrite = {
          description: `Hotel ${room.description}: ${room.comments}`,
          type: ReservationRemarkWriteTypeEnum.Internal,
        };
        data.remarks.push(remark);
      }
    });
  }
  data.location = helpers.getIRI(data.location, '/api/locations/');
  data.bookingEventType = helpers.getIRI(
    data.bookingEventType ?? data.meeting,
    '/api/booking_event_types/',
  );

  data.source = ReservationWriteSourceEnum.Form;
  if (data.miceRemarks) {
    const remark: ReservationRemarkReservationWrite = {
      description: data.miceRemarks,
      type: ReservationRemarkWriteTypeEnum.Internal,
    };
    data.remarks.push(remark);
    delete data.miceRemarks; // Prevent duplicate insertion
  }
  if (data.discountCode) {
    const remark: ReservationRemarkReservationWrite = {
      description: `Discount code: ${data.discountCode}`,
      type: ReservationRemarkWriteTypeEnum.Note,
    };
    data.remarks.push(remark);
    delete data.discountCode;
  }
  if (data.contract) {
    delete data.contract;
  }
  return data as ReservationWrite;
}

function parseReservationData(data: ReservationRead) {
  const reservation: Record<string, any> = Object.assign({}, data);
  if (!data.types?.includes(ReservationReadTypesEnum.Hotel)) {
    delete reservation.rooms;
  }
  if (!data.types?.includes(ReservationReadTypesEnum.Mice)) {
    delete reservation.days;
    delete reservation.meeting;
    delete reservation.eventname;
  }
  reservation.days?.forEach((day: ReservationDayRead) => {
    if (day.halls) {
      day.halls.forEach((hall) => {
        hall.price = hall.priceOverride ?? hall.price;
        hall.hall = hall.hallObject;
      });
    }

    if (day.items) {
      day.items!.forEach((item) => {
        item.time = item.time ?? item.startTime;
      });
    }
  });
  reservation.emailaddress = data.user?.email;
  return reservation;
}

function parseReservationRoomToBookingRoom(
  room: ReservationRoomRead,
  remarks: BookingRemarkBookingWrite[],
): BookingHotelRoomBookingWrite {
  if (room.comments) {
    const remark: BookingRemarkBookingWrite = {
      description: `Hotel ${room.description}: ${room.comments}`,
      type: BookingRemarkBookingWriteTypeEnum.Internal,
    };
    remarks.push(remark);
  }
  return {
    ...room,
    amount: Math.round(room.amount ?? 1),
    paxRoom: Math.round(room.paxRoom ?? 1),
    roomType:
      helpers.getIRI(room.locationRoomType, '/api/location_room_types/') ??
      null,
  } as BookingHotelRoomBookingWrite;
}

function parseReservationToBooking(reservation: ReservationRead): BookingWrite {
  const remarks = [] as BookingRemarkBookingWrite[];
  if (reservation.miceRemarks) {
    const remark: ReservationRemarkReservationWrite = {
      description: reservation.miceRemarks,
      type: ReservationRemarkWriteTypeEnum.Internal,
    };
    remarks.push(remark);
  }
  if (reservation.discountCode) {
    const remark: ReservationRemarkReservationWrite = {
      description: `Discount code: ${reservation.discountCode}`,
      type: ReservationRemarkWriteTypeEnum.Note,
    };
    remarks.push(remark);
  }
  return {
    location: helpers.getIRI(reservation.location, '/api/locations/') ?? null,
    email: reservation.emailaddress,
    types: reservation.types ?? null,
    eventName: reservation.eventname ?? null,
    eventType:
      helpers.getIRI(reservation.meeting, '/api/booking_event_types/') ?? null,
    hotelRooms: reservation.rooms?.map((room) =>
      parseReservationRoomToBookingRoom(room, remarks),
    ),
    meetingRooms: reservation.days?.map(parseReservationDay) ?? null,
    company: reservation.company ?? null,
    country: reservation.country ?? null,
    postalcode: reservation.postalcode ?? null,
    city: reservation.city ?? null,
    address: [reservation.street, reservation.housenumber].join(' ').trim(),
    invoiceReference: reservation.invoiceReference ?? null,
    contactPersonFirstname: reservation.contactpersonFirstname ?? null,
    contactPersonLastname: reservation.contactpersonLastname ?? null,
    contactPersonPhonenumber: reservation.phonenumber ?? null,
    executorContact: null,
    onsiteContact: {
      firstname: reservation.onsiteContact?.firstname ?? null,
      lastname: reservation.onsiteContact?.lastname ?? null,
      email: reservation.onsiteContact?.email ?? null,
      phonenumber: reservation.onsiteContact?.phonenumber ?? null,
    },
    locale: reservation.locale?.slice(0, 2) ?? undefined,
    remarks: remarks,
  };
}

function parseURLParams(
  data: Record<string, any>,
  params: Record<string, string>,
  locations: LocationRead[],
) {
  const reservation: ReservationRead = Object.assign({}, data);
  const hasMice = (params?.mice ?? 'true') === 'true'; // fallback to true
  const hasHotel = (params?.hotel ?? 'false') === 'true'; // fallback to false

  reservation.types = [];
  if (hasMice) {
    reservation.types.push(ReservationReadTypesEnum.Mice);
  }
  if (hasHotel) {
    reservation.types.push(ReservationReadTypesEnum.Hotel);
  }

  reservation.company = params.company;
  reservation.country = params.country;

  reservation.street = params.address;
  reservation.housenumber = params.houseNumber;
  reservation.postalcode = params.postalCode;
  reservation.city = params.city;
  reservation.contactpersonFirstname = params.firstName;
  reservation.contactpersonLastname = params.lastName;
  reservation.phonenumber = params.phone;
  reservation.emailaddress = params.email;
  reservation.discountCode = params.promo;

  if (params.locationID) {
    const locationID = parseInt(params.locationID, 10);
    reservation.location = locations.find(
      (location: LocationRead) => location.id === locationID,
    ) as LocationReservationRead;
  }
  const defaultHall: ReservationDayHallRead = {};
  const pax = params.pax ? parseInt(params.pax, 10) : 1;
  if (params.pax) {
    defaultHall.pax = pax;
  }
  const startDate = params.startDate;
  const endDate = params.endDate ?? startDate;
  if (startDate) {
    reservation.days = [];
    reservation.rooms = [];
    const start = parseISO(startDate);
    const end = parseISO(endDate);
    const interval = eachDayOfInterval({ start, end });
    if (hasMice) {
      for (const day of interval) {
        reservation.days.push({
          date: format(day, 'yyyy-MM-dd'),
          halls: [cloneDeep(defaultHall)],
          packageDeals: [],
          items: [],
        });
      }
    }
    if (hasHotel) {
      const perRoom = params.perRoom ? parseInt(params.perRoom, 10) : 2;
      reservation.rooms.push({
        amount: Math.ceil(pax / perRoom),
        startDate: format(start, 'yyyy-MM-dd'),
        endDate: format(add(end, { days: 1 }), 'yyyy-MM-dd'),
        paxRoom: perRoom,
        type: 'standard',
        breakfast: true,
        description: reservation.company ?? '',
      });
    }
  }

  return reservation;
}

export type reservationState = ReturnType<typeof getDefaultState>;

export const Reservation: Module<reservationState, State> = {
  namespaced: true,

  state: getDefaultState(),

  mutations: {
    setReservation(state, reservation: ReservationWrite) {
      reservation.locale = state.reservation.locale; // Prevent overwriting locale. Have to use setReservationLocale
      state.reservation = JSON.parse(JSON.stringify(reservation));
    },
    setReservationLoading(state, isLoading: boolean) {
      state.reservationLoading = isLoading;
    },
    setReservationSubmitting(state, isSubmitting: boolean) {
      state.reservationSubmitting = isSubmitting;
    },
    setConfig(state, config: Record<string, any>) {
      state.config = config;
    },
    setReservationLocale(state, locale: string) {
      state.reservation.locale = locale;
    },
    setBookingId(state, id: number | null | undefined) {
      state.bookingId = id;
    },
    setBookingToken(state, token: string) {
      state.bookingToken = token;
    },
    setBookingTransitions(state, transitions: string[]) {
      state.transitions = transitions;
    },
  },

  actions: {
    async getReservation(store, { id }) {
      const reservationApi = getReservationApiBypass();
      const response = await reservationApi.reservationsIdGet(
        { id },
        store.state.config,
      );
      return parseReservationData(response.data);
    },

    async postReservation(store) {
      const api = getReservationApi();
      const reservationData = parseReservationDataBeforeRequest(
        store.state.reservation,
      );
      const response = await api.reservationsPost({
        reservationWrite: reservationData,
      });
      return response.data;
    },

    async putReservation(store) {
      const api = getReservationApiBypass();
      const reservationData = parseReservationDataBeforeRequest(
        store.state.reservation,
      );
      const response = await api.reservationsIdPut(
        {
          id: store.state.reservation.id!.toString(),
          reservationWrite: reservationData,
        },
        store.state.config,
      );
      return response.data;
    },

    async getBooking(_, { id, username, key }) {
      const api = getBookingApi();
      let response;
      if (username && key) {
        response = await api.reservationsBookingConfirmationGet(
          { id },
          { headers: { username, key } },
        );
      } else {
        response = await api.reservationsBookingBillingGet(
          { id },
          { withCredentials: true },
        );
      }
      return parseBookingData(response.data);
    },

    async postBooking(store) {
      const api = getBookingApi();
      const data = parseReservationToBooking(store.state.reservation);
      const response = await api.reservationsBookingPost(
        {
          bookingWrite: data,
        },
        { withCredentials: true },
      );
      store.commit('setBookingId', response.data.id);
    },

    async putBooking(store) {
      const api = getBookingApi();
      const data = parseReservationToBooking(store.state.reservation);
      const response = await api.reservationsBookingPut(
        {
          id: store.state.bookingId!.toString(),
          bookingWrite: data,
        },
        { withCredentials: true },
      );
      store.commit('setBookingTransitions', response.data.transitions);
    },

    async setBookingStatus(store, status: string) {
      const api = getBookingApi();
      await api.bookingUpdateStatus(
        {
          id: store.state.bookingId!.toString(),
          bookingJsonldBookingStatusWrite: {
            transition: status,
          },
        },
        { withCredentials: true },
      );
    },

    setReservationLoading({ commit }, isLoading: boolean) {
      commit('setReservationLoading', isLoading);
    },

    setReservationSubmitting({ commit }, isSubmitting: boolean) {
      commit('setReservationSubmitting', isSubmitting);
    },

    async parseRouteParams(
      store,
      p: { params: Record<string, any>; locations: LocationRead[] },
    ) {
      return parseURLParams(store.state.reservation, p.params, p.locations);
    },
  },
};
